import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { withdrawalService } from '@/services/withdrawalService'
import type { CreateWithdrawalRequest } from '@/types/withdrawal'

export function useMyWithdrawals(userId: string, status?: string) {
  return useQuery({
    queryKey: ['my-withdrawals', userId, status],
    queryFn: () => withdrawalService.getMyWithdrawals(userId, status),
    enabled: !!userId,
  })
}

export function useWithdrawalRequest(id: string) {
  return useQuery({
    queryKey: ['withdrawal', id],
    queryFn: () => withdrawalService.getWithdrawalRequest(id),
    enabled: !!id,
  })
}

export function usePendingWithdrawals() {
  return useQuery({
    queryKey: ['withdrawals', 'pending'],
    queryFn: () => withdrawalService.getPendingWithdrawals(),
  })
}

export function useUserWithdrawals(userId: string, status?: string) {
  return useQuery({
    queryKey: ['withdrawals', 'user', userId, status],
    queryFn: () => withdrawalService.getUserWithdrawals(userId, status),
    enabled: !!userId,
  })
}

export function useRequestWithdrawal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateWithdrawalRequest) => withdrawalService.requestWithdrawal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-withdrawals'] })
      queryClient.invalidateQueries({ queryKey: ['withdrawal-summary'] })
      queryClient.invalidateQueries({ queryKey: ['my-balance'] })
    },
  })
}

export function useApproveWithdrawal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, adminId }: { id: string; adminId: string }) =>
      withdrawalService.approveWithdrawal(id, adminId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] })
      queryClient.invalidateQueries({ queryKey: ['withdrawal'] })
    },
  })
}

export function useRejectWithdrawal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, adminId, reason }: { id: string; adminId: string; reason: string }) =>
      withdrawalService.rejectWithdrawal(id, adminId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] })
      queryClient.invalidateQueries({ queryKey: ['withdrawal'] })
    },
  })
}

export function useCompleteWithdrawal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, receiptUrl, taxDeduction }: { id: string; receiptUrl: string; taxDeduction?: number }) =>
      withdrawalService.completeWithdrawal(id, receiptUrl, taxDeduction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] })
      queryClient.invalidateQueries({ queryKey: ['withdrawal'] })
    },
  })
}
